/**
 * LLM Budget — mirrors apps/experts/public/admin/llm-budget.html. GET
 * /api/admin/llm-budget returns the current-hour per-provider spend/cap/headroom
 * + circuit state, the two kill-switch flags, and a 24h rollup. Auto-refreshes
 * (refetchInterval on the query) like the original's polling.
 */
import {
  AdminError,
  AdminLoading,
  AdminPageHeader,
  Mono,
  Panel,
  StatusDot,
  Table,
  Td,
  Th,
  toneForStatus,
  Tr,
} from '../../components/primitives';
import { useLlmBudget } from '../../data-access/useAdmin';
import { relTime } from '../../lib/format';

function fmtAmount(n: number | null, pricing: string): string {
  if (n == null) return '—';
  return pricing === 'calls_per_hr'
    ? String(Math.round(n))
    : `$${n.toFixed(2)}`;
}

export function LlmBudgetPage() {
  const { data, isPending, isError, error } = useLlmBudget();

  return (
    <div>
      <AdminPageHeader
        subtitle={
          data
            ? `env ${data.env} · hour ${relTime(data.hour_bucket)} · generated ${relTime(data.generated_at)}`
            : 'Per-provider LLM spend, caps, and circuit state'
        }
        title="LLM Budget"
      />

      {isPending ? <AdminLoading /> : null}
      {isError ? (
        <AdminError
          message={`Failed to load: ${(error as Error)?.message ?? 'unknown'}`}
        />
      ) : null}

      {data ? (
        <div className="flex flex-col gap-4">
          <Panel title="Kill switches">
            <ul className="flex flex-wrap gap-6 font-inter text-sm">
              <li className="flex items-center gap-2">
                <StatusDot tone={data.enabled ? 'ok' : 'muted'} />
                Budget enforcement: {data.enabled ? 'on' : 'off'}
              </li>
              <li className="flex items-center gap-2">
                <StatusDot tone={data.kill_switch_all_llms ? 'error' : 'ok'} />
                Kill-switch all LLMs:{' '}
                {data.kill_switch_all_llms ? 'ENGAGED' : 'off'}
              </li>
            </ul>
          </Panel>

          <Panel title="Providers (current hour)">
            <Table
              head={
                <>
                  <Th>Provider</Th>
                  <Th>Pricing</Th>
                  <Th>Spend</Th>
                  <Th>Cap</Th>
                  <Th>Headroom</Th>
                  <Th>Calls</Th>
                  <Th>Tokens (in/out)</Th>
                  <Th>Circuit</Th>
                </>
              }
            >
              {data.providers.map((p) => (
                <Tr key={p.provider}>
                  <Td>
                    <Mono>{p.provider}</Mono>
                  </Td>
                  <Td className="text-content-secondary text-xs">
                    {p.pricing}
                  </Td>
                  <Td>{fmtAmount(p.spend, p.pricing)}</Td>
                  <Td>{fmtAmount(p.cap, p.pricing)}</Td>
                  <Td>{fmtAmount(p.headroom, p.pricing)}</Td>
                  <Td>{p.calls}</Td>
                  <Td className="text-content-secondary text-xs">
                    {p.input_tokens.toLocaleString()} /{' '}
                    {p.output_tokens.toLocaleString()}
                  </Td>
                  <Td>
                    <span className="inline-flex items-center gap-1.5">
                      <StatusDot tone={toneForStatus(p.circuit_state)} />
                      {p.circuit_state}
                    </span>
                  </Td>
                </Tr>
              ))}
            </Table>
          </Panel>

          <Panel title="Last 24 hours">
            {data.last_24h.length === 0 ? (
              <p className="font-inter text-content-tertiary text-sm">
                No spend in the last 24h.
              </p>
            ) : (
              <Table
                head={
                  <>
                    <Th>Hour</Th>
                    <Th>Anthropic</Th>
                    <Th>OpenAI</Th>
                    <Th>Cohere</Th>
                  </>
                }
              >
                {data.last_24h.map((row) => (
                  <Tr key={row.hour}>
                    <Td className="text-content-secondary text-xs">
                      {relTime(row.hour)}
                    </Td>
                    <Td>
                      {row.anthropic
                        ? `$${row.anthropic.spend.toFixed(2)} (${row.anthropic.calls})`
                        : '—'}
                    </Td>
                    <Td>
                      {row.openai
                        ? `$${row.openai.spend.toFixed(2)} (${row.openai.calls})`
                        : '—'}
                    </Td>
                    <Td>{row.cohere ? `${row.cohere.calls} calls` : '—'}</Td>
                  </Tr>
                ))}
              </Table>
            )}
          </Panel>
        </div>
      ) : null}
    </div>
  );
}

export default LlmBudgetPage;
